<template>
    <div v-if="isImpersonating" class="impersonation-banner">
        <div class="impersonation-banner-content">
            <i class="fas fa-user-secret me-2"></i>
            <span>Impersonating: <strong>{{ impersonatedName }}</strong></span>
        </div>
        <button class="impersonation-banner-stop" @click="stopImpersonating" :disabled="stopping">
            <i class="fas fa-times-circle me-1"></i>
            {{ stopping ? 'Stopping...' : 'Stop Impersonating' }}
        </button>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useAppStore } from '@/stores/AppStore';
import TokenHelper from '@/services/TokenHelper';
import ApiService from '@/services/ApiService';

const appStore = useAppStore();
const stopping = ref(false);
const isImpersonatingValue = ref(false);

onMounted(async () => {
    isImpersonatingValue.value = await TokenHelper.isImpersonating();
});

const isImpersonating = computed(() => isImpersonatingValue.value || !!appStore.impersonatedName);
const impersonatedName = computed(() => appStore.impersonatedName || 'Unknown User');

const stopImpersonating = async () => {
    stopping.value = true;
    try {
        const logId = await TokenHelper.getImpersonationLogId();
        if (logId) {
            await ApiService.endImpersonation(logId);
        }
        await appStore.clearImpersonation();
        window.location.reload();
    } catch (error) {
        console.error('Error stopping impersonation:', error);
        await appStore.clearImpersonation();
        window.location.reload();
    } finally {
        stopping.value = false;
    }
};
</script>

<style scoped>
.impersonation-banner {
    position: relative;
    background: linear-gradient(90deg, #d63031, #e17055);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 16px;
    font-size: 0.875rem;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.impersonation-banner-content {
    display: flex;
    align-items: center;
}

.impersonation-banner-stop {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.5);
    color: #fff;
    border-radius: 4px;
    padding: 3px 10px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: background 0.15s;
}

.impersonation-banner-stop:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.35);
}

.impersonation-banner-stop:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}
</style>
